/**
 * Blocktickets Stripe Connect fee engine
 *
 * Purpose:
 * - Calculate a customer charge amount for destination charges
 * - Include Blocktickets service fee
 * - Include an estimated Stripe processing fee
 * - Set application_fee_amount so the connected account keeps face value
 *
 * Notes:
 * - This is designed for Stripe Connect destination charges
 * - Stripe fees are charged to the platform in this model
 * - The processing fee calculation here is an estimate based on your pricing config
 * - Reconcile actual Stripe fees later using balance transactions / webhooks
 */
export class FeeCalculationError extends Error {
    details;
    constructor(message, details = {}) {
        super(message);
        this.name = 'FeeCalculationError';
        this.details = details;
    }
}
/**
 * Safely convert a decimal dollar amount to integer cents.
 * Example: 10.88 -> 1088
 */
export function toCents(amount) {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) {
        throw new FeeCalculationError('Amount must be a finite number', { amount });
    }
    return Math.round(amount * 100);
}
/**
 * Convert integer cents to decimal dollars.
 * Example: 1088 -> 10.88
 */
export function fromCents(cents) {
    if (!Number.isInteger(cents)) {
        throw new FeeCalculationError('Cents must be an integer', { cents });
    }
    return cents / 100;
}
/**
 * Round up division for integer math.
 * Example: ceilDiv(1001, 100) => 11
 */
export function ceilDiv(numerator, denominator) {
    return Math.floor((numerator + denominator - 1) / denominator);
}
/**
 * Estimate the Stripe processing fee in cents for a given charge amount.
 *
 * fee = round_up(charge * percent + fixed)
 *
 * percentBps is basis points:
 * - 290 = 2.90%
 * - 300 = 3.00%
 */
export function estimateStripeFeeCents(chargeAmountCents, pricing) {
    if (!Number.isInteger(chargeAmountCents) || chargeAmountCents < 0) {
        throw new FeeCalculationError('chargeAmountCents must be a non-negative integer', {
            chargeAmountCents,
        });
    }
    const { percentBps, fixedFeeCents } = pricing;
    if (!Number.isInteger(percentBps) || percentBps < 0 || percentBps >= 10000) {
        throw new FeeCalculationError('percentBps must be an integer between 0 and 9999', {
            percentBps,
        });
    }
    if (!Number.isInteger(fixedFeeCents) || fixedFeeCents < 0) {
        throw new FeeCalculationError('fixedFeeCents must be a non-negative integer', {
            fixedFeeCents,
        });
    }
    // Round up to avoid under-collecting by a cent
    return Math.ceil((chargeAmountCents * percentBps) / 10000) + fixedFeeCents;
}
/**
 * Solve for the minimum customer charge such that:
 *
 * chargeAmount
 *   - estimatedStripeFee(chargeAmount)
 *   - serviceFee
 * = faceValue
 *
 * Rearranged conceptually:
 * chargeAmount = faceValue + serviceFee + estimatedStripeFee(chargeAmount)
 *
 * Because Stripe fee depends on charge amount, we solve using integer math.
 */
export function calculateDestinationChargeFees({ ticketPriceCents, serviceFeeCents, stripePricing = {
    percentBps: 290,
    fixedFeeCents: 30, // $0.30
}, currency = 'usd', minimumChargeCents = 50, maxChargeCents = 99999999, metadata = {}, }) {
    if (!Number.isInteger(ticketPriceCents) || ticketPriceCents <= 0) {
        throw new FeeCalculationError('ticketPriceCents must be a positive integer', {
            ticketPriceCents,
        });
    }
    if (!Number.isInteger(serviceFeeCents) || serviceFeeCents < 0) {
        throw new FeeCalculationError('serviceFeeCents must be a non-negative integer', {
            serviceFeeCents,
        });
    }
    const { percentBps, fixedFeeCents } = stripePricing;
    // We want:
    // charge - stripeFee(charge) - serviceFee = ticketPrice
    //
    // Using fee ≈ ceil(charge * p) + fixed
    //
    // A safe closed-form lower-bound:
    // charge >= (ticket + service + fixed) / (1 - p)
    //
    // Integer form:
    // charge >= ceil((ticket + service + fixed) * 10000 / (10000 - percentBps))
    const numerator = (ticketPriceCents + serviceFeeCents + fixedFeeCents) * 10000;
    const denominator = 10000 - percentBps;
    if (denominator <= 0) {
        throw new FeeCalculationError('Invalid Stripe pricing: percent too high', {
            percentBps,
        });
    }
    let chargeAmountCents = ceilDiv(numerator, denominator);
    // Because fee uses rounding, bump until net-to-connected is protected
    while (true) {
        const estimatedStripeFeeCents = estimateStripeFeeCents(chargeAmountCents, stripePricing);
        const applicationFeeAmountCents = serviceFeeCents + estimatedStripeFeeCents;
        const connectedAccountGrossAfterAppFeeCents = chargeAmountCents - applicationFeeAmountCents;
        if (connectedAccountGrossAfterAppFeeCents >= ticketPriceCents) {
            break;
        }
        chargeAmountCents += 1;
    }
    if (chargeAmountCents < minimumChargeCents) {
        throw new FeeCalculationError('Calculated charge is below minimum charge threshold', {
            chargeAmountCents,
            minimumChargeCents,
        });
    }
    if (chargeAmountCents > maxChargeCents) {
        throw new FeeCalculationError('Calculated charge exceeds maximum allowed charge', {
            chargeAmountCents,
            maxChargeCents,
        });
    }
    const estimatedStripeFeeCents = estimateStripeFeeCents(chargeAmountCents, stripePricing);
    const applicationFeeAmountCents = serviceFeeCents + estimatedStripeFeeCents;
    const destinationReceivesCents = chargeAmountCents - applicationFeeAmountCents;
    const buyerProcessingFeeCents = estimatedStripeFeeCents;
    return {
        currency: currency.toLowerCase(),
        ticketPriceCents,
        serviceFeeCents,
        buyerProcessingFeeCents,
        chargeAmountCents,
        applicationFeeAmountCents,
        destinationReceivesCents,
        stripePricing: {
            percentBps,
            fixedFeeCents,
        },
        // Helpful display fields
        display: {
            ticketPrice: fromCents(ticketPriceCents),
            serviceFee: fromCents(serviceFeeCents),
            buyerProcessingFee: fromCents(buyerProcessingFeeCents),
            totalCharge: fromCents(chargeAmountCents),
            applicationFee: fromCents(applicationFeeAmountCents),
            destinationReceives: fromCents(destinationReceivesCents),
        },
        metadata: {
            ...metadata,
            fee_model: 'destination_charge',
            service_fee_cents: String(serviceFeeCents),
            estimated_processing_fee_cents: String(estimatedStripeFeeCents),
            ticket_price_cents: String(ticketPriceCents),
        },
    };
}
/**
 * Build a Stripe PaymentIntent payload for destination charges.
 *
 * Stripe docs for destination charges show using:
 * - amount
 * - currency
 * - transfer_data[destination]
 * - application_fee_amount
 * - optionally on_behalf_of (CAD orgs only; required for cross-border CA settlement)
 */
export function buildDestinationChargePaymentIntentParams({ connectedAccountId, ticketPriceCents, serviceFeeCents, stripePricing, currency = 'usd', onBehalfOf = null, customerId = null, paymentMethodId = null, confirm = false, captureMethod = 'automatic', description = '', metadata = {}, }) {
    if (!connectedAccountId || typeof connectedAccountId !== 'string') {
        throw new FeeCalculationError('connectedAccountId is required', {
            connectedAccountId,
        });
    }
    const feeResult = calculateDestinationChargeFees({
        ticketPriceCents,
        serviceFeeCents,
        stripePricing,
        currency,
        metadata,
    });
    const params = {
        amount: feeResult.chargeAmountCents,
        currency: feeResult.currency,
        application_fee_amount: feeResult.applicationFeeAmountCents,
        transfer_data: {
            destination: connectedAccountId,
        },
        capture_method: captureMethod,
        confirm,
        description,
        metadata: feeResult.metadata,
    };
    if (onBehalfOf && String(currency || 'usd').toLowerCase() === 'cad') {
        params.on_behalf_of = onBehalfOf;
    }
    if (customerId) {
        params.customer = customerId;
    }
    if (paymentMethodId) {
        params.payment_method = paymentMethodId;
    }
    return {
        feeResult,
        paymentIntentParams: params,
    };
}
